import { Fragment, useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { canaliAPI, settoriMerceologiciAPI } from '@offertify/api-client';
import { promozioniAPI } from '../utils/api';
import { Promozione } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import ColorChipPicker, { ColorChipItem } from '../components/ColorChipPicker';
import { contrastColor } from '../utils/color';
import toast from 'react-hot-toast';
import {
  Plus,
  Search,
  Trash2,
  Loader2,
  Percent,
  DollarSign,
  Tag,
  Package,
  Users,
  Paperclip,
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
} from 'lucide-react';

type SortField = 'nome' | 'tipo' | 'target_tipo' | 'valore' | 'valido_da' | 'is_attiva' | 'priorita';
type SortDir = 'asc' | 'desc';
type GroupBy = 'none' | 'canale' | 'settore';

interface ListPrefs {
  sortBy: SortField;
  sortDir: SortDir;
  groupBy: GroupBy;
}

// Ordinamento e raggruppamento persistiti per browser (preferenza personale).
const PREFS_KEY = 'offertify:promotions:list-prefs';
const DEFAULT_PREFS: ListPrefs = { sortBy: 'priorita', sortDir: 'desc', groupBy: 'none' };

function loadPrefs(): ListPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...DEFAULT_PREFS, ...JSON.parse(raw) } : DEFAULT_PREFS;
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs: ListPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // storage non disponibile (es. navigazione privata): la preferenza vale solo per la sessione
  }
}

const NO_GROUP_ID = '__none__';

interface PromoGroup {
  key: string;
  item: ColorChipItem | null;
  promos: Promozione[];
}

/** Raggruppa le promozioni per canale/settore; una promo con più valori compare in ogni gruppo. */
function groupPromozioni(promozioni: Promozione[], groupBy: Exclude<GroupBy, 'none'>): PromoGroup[] {
  const groups = new Map<string, PromoGroup>();
  const ungrouped: Promozione[] = [];
  for (const promo of promozioni) {
    const items: ColorChipItem[] = (groupBy === 'canale' ? promo.canali : promo.settori) || [];
    if (items.length === 0) ungrouped.push(promo);
    for (const item of items) {
      const group = groups.get(item.id) ?? { key: item.id, item, promos: [] };
      group.promos.push(promo);
      groups.set(item.id, group);
    }
  }
  const sorted = [...groups.values()].sort((a, b) => a.item!.nome.localeCompare(b.item!.nome));
  if (ungrouped.length > 0) sorted.push({ key: NO_GROUP_ID, item: null, promos: ungrouped });
  return sorted;
}

function Chip({ item }: { item: ColorChipItem }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: item.color, color: contrastColor(item.color) }}
    >
      {item.nome}
    </span>
  );
}

export default function Promotions() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('promozioni:edit');
  const canCreate = hasPermission('promozioni:create');
  const canDelete = hasPermission('promozioni:delete');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [targetFilter, setTargetFilter] = useState<string>('');
  const [canaleFilter, setCanaleFilter] = useState<string[]>([]);
  const [settoreFilter, setSettoreFilter] = useState<string[]>([]);
  const [prefs, setPrefs] = useState<ListPrefs>(loadPrefs);
  // Raggruppando, la pagina è più ampia così i gruppi risultano più completi
  const limit = prefs.groupBy === 'none' ? 20 : 100;

  useEffect(() => savePrefs(prefs), [prefs]);

  const canViewCanali = hasPermission('canali:view');
  const canViewSettori = hasPermission('settori:view');

  const { data: canali } = useQuery({
    queryKey: ['canali-select'],
    queryFn: async () => (await canaliAPI.getCanaliForSelect()).data,
    enabled: canViewCanali,
  });
  const { data: settori } = useQuery({
    queryKey: ['settori-merceologici-select'],
    queryFn: async () => (await settoriMerceologiciAPI.getSettoriForSelect()).data,
    enabled: canViewSettori,
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      'promozioni', page, limit, search, statusFilter, targetFilter,
      canaleFilter, settoreFilter, prefs.sortBy, prefs.sortDir,
    ],
    queryFn: async () => {
      const params: Parameters<typeof promozioniAPI.getPromozioni>[0] = {
        page,
        limit,
        sort_by: prefs.sortBy,
        sort_dir: prefs.sortDir,
      };
      if (search) params.search = search;
      if (statusFilter === 'attive') params.is_attiva = true;
      if (statusFilter === 'inattive') params.is_attiva = false;
      if (targetFilter) params.target_tipo = targetFilter;
      if (canaleFilter.length) params.canale_id = canaleFilter;
      if (settoreFilter.length) params.settore_id = settoreFilter;
      const res = await promozioniAPI.getPromozioni(params);
      return res.data;
    },
  });

  const toggleSort = (field: SortField) => {
    setPrefs((prev) =>
      prev.sortBy === field
        ? { ...prev, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' }
        : { ...prev, sortBy: field, sortDir: 'asc' }
    );
    setPage(1);
  };

  const hasActiveFilters =
    !!search || !!statusFilter || !!targetFilter || canaleFilter.length > 0 || settoreFilter.length > 0;

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTargetFilter('');
    setCanaleFilter([]);
    setSettoreFilter([]);
    setPage(1);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => promozioniAPI.deletePromozione(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promozioni'] });
      toast.success('Promozione eliminata');
    },
    onError: () => toast.error("Errore nell'eliminazione"),
  });

  const promozioni = data?.data || [];
  const pagination = data?.pagination;

  const groups = useMemo(
    () => (prefs.groupBy === 'none' ? null : groupPromozioni(promozioni, prefs.groupBy)),
    [promozioni, prefs.groupBy]
  );
  const noGroupLabel = prefs.groupBy === 'canale' ? 'Senza canale' : 'Senza settore';

  const getStatusBadge = (promo: Promozione) => {
    const today = new Date().toISOString().split('T')[0];
    if (!promo.is_attiva) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Inattiva</span>;
    }
    if (promo.valido_da > today) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Programmata</span>;
    }
    if (promo.valido_a && promo.valido_a < today) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Scaduta</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Attiva</span>;
  };

  const getTargetLabel = (promo: Promozione) => {
    if (promo.target_tipo === 'offerta') return 'Offerta (manuale)';
    if (promo.target_tipo === 'prodotto') return promo.target_prodotto_nome || 'Prodotto';
    if (promo.target_tipo === 'categoria') return promo.target_categoria || 'Categoria';
    if (promo.target_tipo === 'marca') {
      const marche =
        promo.target_marche && promo.target_marche.length > 0
          ? promo.target_marche
          : promo.target_marca
            ? [promo.target_marca]
            : [];
      return marche.length > 0 ? marche.join(', ') : 'Marca';
    }
    return '';
  };

  const getTargetIcon = (target: string) => {
    if (target === 'prodotto') return <Package className="h-4 w-4 text-gray-400" />;
    if (target === 'categoria') return <Tag className="h-4 w-4 text-gray-400" />;
    return <Tag className="h-4 w-4 text-gray-400" />;
  };

  const formatValore = (promo: Promozione) => {
    if (promo.tipo === 'tag_temporale') return '—';
    if (promo.tipo === 'sconto_percentuale') return `${promo.valore}%`;
    return `${Number(promo.valore).toFixed(2)} EUR`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promozioni</h1>
          <p className="text-sm text-gray-500 mt-1">Gestisci sconti e promozioni</p>
        </div>
        {canCreate && (
          <button
            onClick={() => navigate('/promotions/new')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Nuova Promozione
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca promozioni..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Tutti gli stati</option>
          <option value="attive">Attive</option>
          <option value="inattive">Inattive</option>
        </select>
        <select
          value={targetFilter}
          onChange={(e) => { setTargetFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Tutti i target</option>
          <option value="prodotto">Prodotto</option>
          <option value="categoria">Categoria</option>
          <option value="offerta">Offerta (manuale)</option>
                <option value="marca">Marca</option>
        </select>
        {(canViewCanali || canViewSettori) && (
          <select
            value={prefs.groupBy}
            onChange={(e) => setPrefs((prev) => ({ ...prev, groupBy: e.target.value as GroupBy }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            aria-label="Raggruppa per"
          >
            <option value="none">Nessun raggruppamento</option>
            {canViewCanali && <option value="canale">Raggruppa per canale</option>}
            {canViewSettori && <option value="settore">Raggruppa per settore</option>}
          </select>
        )}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <X className="h-4 w-4" />
            Azzera filtri
          </button>
        )}
      </div>

      {((canali && canali.length > 0) || (settori && settori.length > 0)) && (
        <div className="space-y-2">
          {canali && canali.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-20 text-xs font-medium text-gray-500 uppercase tracking-wider">Canale</span>
              <ColorChipPicker
                items={canali}
                selected={canaleFilter}
                onChange={(ids) => { setCanaleFilter(ids); setPage(1); }}
              />
            </div>
          )}
          {settori && settori.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-20 text-xs font-medium text-gray-500 uppercase tracking-wider">Settore</span>
              <ColorChipPicker
                items={settori}
                selected={settoreFilter}
                onChange={(ids) => { setSettoreFilter(ids); setPage(1); }}
              />
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : promozioni.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Percent className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessuna promozione</h3>
          <p className="text-gray-500 mb-4">Crea la tua prima promozione per offrire sconti ai clienti.</p>
          {canCreate && (
            <button
              onClick={() => navigate('/promotions/new')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Nuova Promozione
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTh field="nome" label="Nome" prefs={prefs} onSort={toggleSort} />
                <SortableTh field="tipo" label="Tipo" prefs={prefs} onSort={toggleSort} />
                <SortableTh field="target_tipo" label="Target" prefs={prefs} onSort={toggleSort} />
                <SortableTh field="valore" label="Valore" prefs={prefs} onSort={toggleSort} />
                <SortableTh field="valido_da" label="Validita" prefs={prefs} onSort={toggleSort} />
                <SortableTh field="is_attiva" label="Stato" prefs={prefs} onSort={toggleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Canali / Settori</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clienti</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Allegati</th>
                <SortableTh field="priorita" label="Priorita" prefs={prefs} onSort={toggleSort} />
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(groups ?? [{ key: 'all', item: null, promos: promozioni }]).map((group) => (
                <Fragment key={group.key}>
                  {groups && (
                    <tr className="bg-gray-50">
                      <td colSpan={COLUMN_COUNT} className="px-6 py-2">
                        <div className="flex items-center gap-2">
                          {group.item ? (
                            <Chip item={group.item} />
                          ) : (
                            <span className="text-xs font-medium text-gray-500">{noGroupLabel}</span>
                          )}
                          <span className="text-xs text-gray-500">
                            {group.promos.length} promozion{group.promos.length === 1 ? 'e' : 'i'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                  {group.promos.map((promo) => (
                    <tr key={promo.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{promo.nome}</div>
                          {promo.codice && (
                            <div className="text-xs text-gray-500">{promo.codice}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          {promo.tipo === 'tag_temporale' ? (
                            <Tag className="h-4 w-4 text-blue-500" />
                          ) : promo.tipo === 'sconto_percentuale' ? (
                            <Percent className="h-4 w-4 text-blue-500" />
                          ) : (
                            <DollarSign className="h-4 w-4 text-green-500" />
                          )}
                          <span className="text-sm text-gray-700">
                            {promo.tipo === 'tag_temporale' ? 'Tag temporale' : promo.tipo === 'sconto_percentuale' ? 'Sconto %' : 'Prezzo fisso'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          {getTargetIcon(promo.target_tipo)}
                          <span className="text-sm text-gray-700">{getTargetLabel(promo)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900">{formatValore(promo)}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div>{new Date(promo.valido_da).toLocaleDateString('it-IT')}</div>
                        {promo.valido_a && (
                          <div className="text-xs">al {new Date(promo.valido_a).toLocaleDateString('it-IT')}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">{getStatusBadge(promo)}</td>
                      <td className="px-6 py-4">
                        {(promo.canali?.length || 0) + (promo.settori?.length || 0) > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[220px]">
                            {(promo.canali || []).map((c) => <Chip key={c.id} item={c} />)}
                            {(promo.settori || []).map((st) => <Chip key={st.id} item={st} />)}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">
                            {promo.clienti_count === 0 ? 'Tutti' : promo.clienti_count}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {(promo.allegati_count ?? 0) > 0 ? (
                          <div
                            className="inline-flex items-center gap-1.5 text-sm text-gray-700"
                            title={`${promo.allegati_count} allegat${promo.allegati_count === 1 ? 'o' : 'i'}`}
                          >
                            <Paperclip className="h-4 w-4 text-gray-400" />
                            <span>{promo.allegati_count}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{promo.priorita}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/promotions/${promo.id}`)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                            title={canEdit ? 'Modifica' : 'Visualizza'}
                          >
                            {canEdit ? <Edit className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => {
                                if (confirm('Eliminare questa promozione?')) {
                                  deleteMutation.mutate(promo.id);
                                }
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                              title="Elimina"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
              <span className="text-sm text-gray-500">
                {pagination.total} promozioni totali
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-700">
                  {page} / {pagination.pages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
                  disabled={page >= pagination.pages}
                  className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const COLUMN_COUNT = 11;

function SortableTh({
  field,
  label,
  prefs,
  onSort,
}: {
  field: SortField;
  label: string;
  prefs: ListPrefs;
  onSort: (field: SortField) => void;
}) {
  const active = prefs.sortBy === field;
  const Icon = !active ? ArrowUpDown : prefs.sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
      aria-sort={active ? (prefs.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 uppercase hover:text-gray-900 ${active ? 'text-gray-900' : ''}`}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? '' : 'text-gray-300'}`} />
      </button>
    </th>
  );
}
